/* sugar:assets/deal-badge.js@2.0.0 */
/*
 * Deal Badge — the layout work a badge can't do in CSS alone.
 *
 * A Deal Badge sits on a selectable card: an Offer, or an option in a Subscriptions block. The
 * card's script fills the badge's placeholders; this file does the two geometric jobs that need
 * measuring, and both hosts call it:
 *   - window.SugarDealBadge.layoutEdge(card, autoAlign)  edge badges that share a side sit side by side
 *   - window.SugarDealBadge.layoutRing(badge)             the all-around band and its circling text
 *   - window.SugarDealBadge.layoutCard(card, autoAlign)   both, for one card
 * `card` is the element carrying `data-deal-card`; `autoAlign(badge)` returns 'left' | 'center' |
 * 'right' for a badge whose alignment is Auto, since that depends on the host's layout.
 */
(function () {
  if (window.SugarDealBadge) return;

  const BADGE = '.sugar-deal-badge';
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const RING_SPEED = 20;
  const BADGE_GAP = 6;

  /*
   * The badge's text is rich text, so a word the merchant bolded has to stay bold once it circles
   * the card. Reading textContent would flatten it, so the label is read as runs of plain and bold
   * characters, and written back as tspans that pick the theme's bold weight up from the CSS.
   */
  function readLabel(source) {
    const chars = [];
    const walk = (node, bold) => {
      node.childNodes.forEach((n) => {
        if (n.nodeType === 3) {
          for (const ch of n.nodeValue) chars.push({ ch, bold });
        } else if (n.nodeType === 1) {
          const tag = n.tagName.toLowerCase();
          walk(n, bold || tag === 'strong' || tag === 'b');
        }
      });
    };
    if (source) walk(source, false);
    const runs = [];
    const push = (ch, bold) => {
      const last = runs[runs.length - 1];
      if (last && last.bold === bold) last.text += ch;
      else runs.push({ text: ch, bold });
    };
    // Whitespace collapses to a single space, never opens or closes the label, and always sits
    // at the plain weight so a space beside a bold word isn't drawn wider than its neighbours
    let space = false;
    chars.forEach(({ ch, bold }) => {
      if (/\s/.test(ch)) {
        space = runs.length > 0;
        return;
      }
      if (space) push(' ', false);
      space = false;
      push(ch.toUpperCase(), bold);
    });
    return runs;
  }

  function writeLabel(tp, runs) {
    runs.forEach((run) => {
      if (!run.bold) {
        tp.appendChild(document.createTextNode(run.text));
        return;
      }
      const ts = document.createElementNS(SVG_NS, 'tspan');
      ts.setAttribute('class', 'sugar-deal-badge__ring-bold');
      ts.textContent = run.text;
      tp.appendChild(ts);
    });
  }

  /*
   * All-around badges: the label circles the card on a rounded-rectangle path drawn to the
   * badge's size (the card plus the band), traced twice so the text can travel one full lap
   * and wrap without a gap. Labels alternate with dots; the whole thing moves at 20px a
   * second, or not at all for visitors who asked for reduced motion.
   */
  function layoutRing(badge) {
    const card = badge.closest('[data-deal-card]');
    const svg = badge.querySelector(BADGE + '__ring');
    const path = svg && svg.querySelector('path');
    const text = svg && svg.querySelector('text');
    if (!card || !path || !text) return;
    const cs = getComputedStyle(badge);
    const t = parseFloat(cs.getPropertyValue('--ob-ring')) || 16;
    card.style.setProperty('--of-ring', t + 'px');
    const W = badge.offsetWidth;
    const H = badge.offsetHeight;
    if (W < 4 * t || H < 4 * t) return;
    const f = parseFloat(cs.fontSize) || 10;
    // The text's baseline rides the path and the capitals rise about 0.7em above it, so the
    // path sits half a cap height inside the band's centre line to centre the letters.
    const inset = t / 2 + 0.35 * f;
    // The path runs parallel to the band's outer edge, so the text keeps the same distance
    // from that edge through the bends instead of drifting into the card's corner.
    const cardRadius = parseFloat(getComputedStyle(card).borderTopLeftRadius) || 0;
    const r = Math.max(0, cardRadius + t - inset);
    const seg = `H ${W - inset - r} A ${r} ${r} 0 0 1 ${W - inset} ${inset + r} V ${H - inset - r} A ${r} ${r} 0 0 1 ${W - inset - r} ${H - inset} H ${inset + r} A ${r} ${r} 0 0 1 ${inset} ${H - inset - r} V ${inset + r} A ${r} ${r} 0 0 1 ${inset + r} ${inset}`;
    svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
    if (!path.id) path.id = 'ob-ring-' + Math.random().toString(36).slice(2, 8);
    path.setAttribute('d', `M ${inset + r} ${inset} ${seg} ${seg} Z`);
    const lap = path.getTotalLength() / 2;
    const label = readLabel(badge.querySelector(BADGE + '__text'));
    text.textContent = '';
    if (!label.length) return;
    const dot = [{ text: '\u2022', bold: false }];
    const measure = (runs) => {
      const tp = document.createElementNS(SVG_NS, 'textPath');
      tp.setAttribute('href', '#' + path.id);
      writeLabel(tp, runs);
      text.appendChild(tp);
      const w = tp.getComputedTextLength();
      text.removeChild(tp);
      return w;
    };
    const unit = measure(label) + measure(dot) + 28;
    const n = Math.max(1, Math.round(lap / unit));
    const step = lap / (2 * n);
    const still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dur = (lap / RING_SPEED).toFixed(2) + 's';
    for (let k = 1; k <= 2 * n; k++) {
      const tp = document.createElementNS(SVG_NS, 'textPath');
      tp.setAttribute('href', '#' + path.id);
      const start = k * step;
      tp.setAttribute('startOffset', String(start));
      writeLabel(tp, k % 2 ? label : dot);
      if (!still) {
        const a = document.createElementNS(SVG_NS, 'animate');
        a.setAttribute('attributeName', 'startOffset');
        a.setAttribute('repeatCount', 'indefinite');
        a.setAttribute('from', String(start));
        a.setAttribute('to', String(start + lap));
        a.setAttribute('dur', dur);
        tp.appendChild(a);
      }
      text.appendChild(tp);
    }
    badge._ringW = W;
    badge._ringH = H;
    if (!badge._ringRO && window.ResizeObserver) {
      badge._ringRO = new ResizeObserver(() => {
        if (badge.offsetWidth !== badge._ringW || badge.offsetHeight !== badge._ringH) layoutRing(badge);
      });
      badge._ringRO.observe(badge);
    }
  }

  /*
   * Edge badges that share a side would overlap: shift them so they sit side by side in
   * block order, anchored to their side. Auto alignment is the host's call (a list keeps top
   * badges right and bottom badges left, cards centre), passed in as `autoAlign(badge)`.
   */
  function layoutEdge(card, autoAlign) {
    const groups = new Map();
    card.querySelectorAll(BADGE + '[data-ob-pos="top"], ' + BADGE + '[data-ob-pos="bottom"]').forEach((badge) => {
      if (badge.closest('[data-deal-card]') !== card || badge.dataset.obStyle === 'banner') return;
      let align = badge.dataset.obAlign || 'auto';
      if (align === 'auto') align = (autoAlign && autoAlign(badge)) || 'center';
      const key = badge.dataset.obPos + '|' + align;
      if (!groups.has(key)) groups.set(key, { align, badges: [] });
      groups.get(key).badges.push(badge);
    });
    groups.forEach(({ align, badges }) => {
      if (badges.length < 2) { badges.forEach((b) => b.style.removeProperty('--ob-shift')); return; }
      const widths = badges.map((b) => b.offsetWidth);
      const total = widths.reduce((a, w) => a + w, 0) + BADGE_GAP * (badges.length - 1);
      let cursor = 0;
      badges.forEach((badge, i) => {
        let shift;
        if (align === 'right') shift = -(total - cursor - widths[i]);
        else if (align === 'left') shift = cursor;
        else shift = cursor + widths[i] / 2 - total / 2;
        badge.style.setProperty('--ob-shift', Math.round(shift) + 'px');
        cursor += widths[i] + BADGE_GAP;
      });
    });
  }

  function layoutCard(card, autoAlign) {
    layoutEdge(card, autoAlign);
    card.querySelectorAll(BADGE + '[data-ob-pos="all_around"]').forEach((badge) => {
      if (badge.closest('[data-deal-card]') === card) layoutRing(badge);
    });
  }

  window.SugarDealBadge = { layoutEdge, layoutRing, layoutCard };
})();
